"use client";

import { useEffect, useRef, useState } from "react";
import YouTube, { YouTubeProps } from "react-youtube";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { AnimatePresence, motion } from "motion/react";
import { useEcho } from "@laravel/echo-react";
import { useParams } from "react-router-dom";
import { useRealtimeKitMeeting } from "@cloudflare/realtimekit-react";

import { useClassroomStore } from "../store/useClassroomStore";

import { VideoLinkDialog } from "./VideoLinkDialog";

import { Button } from "@/components/ui/button";
import useUserStore from "@/features/auth/store/useUserStore";
import { cn } from "@/lib/utils";

interface VideoSyncViewProps {
  compact?: boolean;
}

interface VideoSyncData {
  videoId: string;
  time: number;
  isPlaying: boolean;
  senderId: string;
  blockedIds: string[];
  isForceSync?: boolean;
}

interface ForceSyncData {
  videoId: string;
  time: number;
  isPlaying: boolean;
  senderId: string;
}

export default function VideoSyncView({ compact = false }: VideoSyncViewProps) {
  const { t } = useTranslation(["common", "classroom"]);
  const { lessonId } = useParams();
  const { user } = useUserStore();
  const playerRef = useRef<any>(null);
  const [isVideoError, setIsVideoError] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  // New state for sync system
  const [showSyncButton, setShowSyncButton] = useState(false);

  // Refs for intervals and timeouts
  const autoSyncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const syncButtonTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Blocked IDs system using refs for instantaneous updates without re-renders
  const blockedIdsRef = useRef<string[]>([]);
  const isProcessingEventRef = useRef(false);

  // Additional refs to prevent force sync loops
  const isProcessingForceSyncRef = useRef(false);
  const lastEventTimeRef = useRef<number>(0);
  const eventDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get store state and functions
  const { videoSync, setVideoSync } = useClassroomStore();

  const isTutor = user?.role === "tutor";
  const isStudent = user?.role === "student";
  const userId = user?.id || "";

  // Get whisper channel for sending events
  const { channel } = useEcho(
    `lesson.${lessonId}`,
    [], // No regular events, only whispers
    () => {},
    [lessonId],
  );

  // Add meeting context for participant events
  const { meeting } = useRealtimeKitMeeting();

  // YouTube player options - now both tutors and students get full controls
  const opts: YouTubeProps["opts"] = {
    height: "100%",
    width: "100%",
    playerVars: {
      autoplay: 1,
      controls: 1, // Both get full controls
      disablekb: 0, // Both can use keyboard
      modestbranding: 1,
      rel: 0,
      playsinline: 1,
      iv_load_policy: 3, // Hide annotations
      cc_load_policy: 0,
      mute: 0, // Don't mute for better experience
    },
  };

  // Send sync event with blocked IDs system and debouncing
  const sendSyncEvent = (
    syncData: Omit<VideoSyncData, "senderId" | "blockedIds" | "isForceSync"> & {
      isForceSync?: boolean;
    },
  ) => {
    if (!channel || !userId) return;

    // Debounce events to prevent spam
    const now = Date.now();

    if (now - lastEventTimeRef.current < 300) {
      // Cancel previous debounced event
      if (eventDebounceTimeoutRef.current) {
        clearTimeout(eventDebounceTimeoutRef.current);
      }
    }

    lastEventTimeRef.current = now;

    // Debounce the actual send
    eventDebounceTimeoutRef.current = setTimeout(() => {
      const eventData: VideoSyncData = {
        ...syncData,
        senderId: userId,
        blockedIds: [...blockedIdsRef.current], // Copy current blocked IDs
        isForceSync: syncData.isForceSync || false,
      };

      console.log("Sending sync event:", eventData);
      channel().whisper("videoSync", eventData);

      // Don't clear blocked IDs here - they will be cleared by the onPlayerStateChange handler
      // after it determines if this was a response to a received sync event
    }, 300);
  };

  // Send force sync event (tutor only)
  const sendForceSyncEvent = () => {
    if (!channel() || !userId || !isTutor || !playerRef.current) return;

    const currentTime = playerRef.current.getCurrentTime();
    const playerState = playerRef.current.getPlayerState();
    const isPlaying = playerState === 1;

    const forceSyncData: ForceSyncData = {
      videoId: videoSync?.videoId || "",
      time: currentTime,
      isPlaying: isPlaying,
      senderId: userId,
    };

    console.log("Sending force sync event:", forceSyncData);
    channel().whisper("forceSync", forceSyncData);
  };

  // Request sync data from tutor (student only)
  const requestSyncFromTutor = () => {
    if (!channel() || !userId || isTutor) return;

    console.log("Requesting sync data from tutor");
    channel().whisper("requestData", { studentId: userId });

    // Hide sync button after requesting
    setShowSyncButton(false);
  };

  // Compare student state with tutor sync data
  const compareWithTutorSync = (tutorData: VideoSyncData) => {
    if (!playerRef.current || !videoSync) return;

    const currentTime = playerRef.current.getCurrentTime();
    const playerState = playerRef.current.getPlayerState();
    const isCurrentlyPlaying = playerState === 1;

    // Check if play/pause state is different
    if (isCurrentlyPlaying !== tutorData.isPlaying) {
      console.log("Play/pause state differs - syncing immediately");
      applySyncData(tutorData, true); // Force sync

      return;
    }

    // Same state - check time difference
    const timeDiff = Math.abs(currentTime - tutorData.time);

    console.log(`Time difference: ${timeDiff} seconds`);

    if (timeDiff > 5) {
      console.log("Time difference > 5 seconds - showing sync button");
      setShowSyncButton(true);

      // Auto-hide sync button after 15 seconds
      if (syncButtonTimeoutRef.current) {
        clearTimeout(syncButtonTimeoutRef.current);
      }
      syncButtonTimeoutRef.current = setTimeout(() => {
        setShowSyncButton(false);
      }, 15000);
    } else {
      // Small difference - hide sync button if shown
      setShowSyncButton(false);
    }
  };

  // Apply sync data to player
  const applySyncData = (data: VideoSyncData, forceSync: boolean = false) => {
    if (!isPlayerReady || !playerRef.current) return;

    // For force sync events, prevent ALL handlers from firing to avoid loops
    if (forceSync) {
      isProcessingEventRef.current = true;
      isProcessingForceSyncRef.current = true;
      console.log("Starting force sync processing - blocking all events");
    }

    // Update store
    setVideoSync({
      videoId: data.videoId,
      time: data.time,
      isPlaying: data.isPlaying,
      lastUpdate: Date.now(),
    });

    const player = playerRef.current;

    // Sync time (always for force sync, or if significant difference)
    const currentTime = player.getCurrentTime();
    const timeDiff = Math.abs(currentTime - data.time);

    if (forceSync || timeDiff > 2) {
      player.seekTo(data.time, true);
    }

    // Sync play state
    const playerState = player.getPlayerState();
    const isCurrentlyPlaying = playerState === 1;

    if (data.isPlaying && !isCurrentlyPlaying) {
      player.playVideo();
    } else if (!data.isPlaying && isCurrentlyPlaying) {
      player.pauseVideo();
    }

    // Hide sync button after successful sync
    if (forceSync) {
      setShowSyncButton(false);
    }

    // Reset processing flags after a longer delay for force sync to ensure all player events have settled
    if (forceSync) {
      setTimeout(() => {
        isProcessingEventRef.current = false;
        isProcessingForceSyncRef.current = false;
        console.log("Ended force sync processing - allowing events");
      }, 1000); // Increased to 1 second to ensure stability
    }
  };

  // Handle sync data received from whisper events
  const handleReceivedSyncData = (data: VideoSyncData) => {
    console.log("Received sync event:", data);

    try {
      if (data.blockedIds.includes(userId)) {
        console.log("Ignoring event - our ID is in blocked IDs");

        return;
      }
    } catch (error) {}

    // Add sender's ID to blocked IDs to prevent echo
    blockedIdsRef.current = [...blockedIdsRef.current, data.senderId];

    if (data.isForceSync) {
      // Force sync events: immediately apply to everyone (both tutors and students)
      console.log("Received force sync event - applying immediately");
      applySyncData(data, true);
    } else {
      // Auto-sync events: use existing logic
      if (isTutor) {
        // Tutors just update their store but don't sync player for auto-sync
        setVideoSync({
          videoId: data.videoId,
          time: data.time,
          isPlaying: data.isPlaying,
          lastUpdate: Date.now(),
        });
      } else {
        // Students compare and decide how to sync for auto-sync
        compareWithTutorSync(data);
      }
    }
  };

  // Handle force sync event (students only)
  const handleForceSyncEvent = (data: ForceSyncData) => {
    console.log("Received force sync event:", data);

    if (isTutor) return; // Only students should handle force sync

    applySyncData(
      {
        ...data,
        blockedIds: [], // Force sync doesn't use blocked IDs
      },
      true,
    );
  };

  // Handle sync data request (tutors only)
  const handleSyncDataRequest = (data: { studentId: string }) => {
    console.log("Received sync data request from:", data.studentId);

    if (!isTutor) return; // Only tutors should handle this

    // Send force sync with current player state
    sendForceSyncEvent();
  };

  // Set up tutor auto-sync and student participant joined handler
  useEffect(() => {
    if (!isTutor || !videoSync || !isPlayerReady) return;

    // Set up 30-second interval for auto-sync (tutors only)
    autoSyncIntervalRef.current = setInterval(() => {
      // Only send auto-sync if we're not processing any events
      if (playerRef.current && !isProcessingEventRef.current && !isProcessingForceSyncRef.current) {
        const currentTime = playerRef.current.getCurrentTime();
        const playerState = playerRef.current.getPlayerState();
        const isPlaying = playerState === 1;

        // Clear any stale blocked IDs before sending auto-sync
        blockedIdsRef.current = [];

        console.log("Sending auto-sync event from tutor");
        sendSyncEvent({
          videoId: videoSync.videoId,
          time: currentTime,
          isPlaying: isPlaying,
          isForceSync: false, // Auto-sync events are not force sync
        });
      } else {
        console.log("Skipping auto-sync - currently processing events");
      }
    }, 30000); // 30 seconds

    return () => {
      if (autoSyncIntervalRef.current) {
        clearInterval(autoSyncIntervalRef.current);
      }
    };
  }, [isTutor, videoSync, isPlayerReady, sendSyncEvent]);

  // Student participant joined handler
  useEffect(() => {
    if (!isStudent || !meeting || !videoSync || !isPlayerReady) return;

    const onParticipantJoined = (participant: any) => {
      console.log("Participant joined (student view):", participant);

      // Only send sync data if we have a video playing and a tutor joined
      if (playerRef.current && videoSync) {
        const currentTime = playerRef.current.getCurrentTime();
        const playerState = playerRef.current.getPlayerState();
        const isPlaying = playerState === 1;

        // Only send if video is actually playing
        if (isPlaying) {
          console.log("Sending video sync to new tutor participant:", participant.id);

          // Send the current video state via whisper after a small delay to ensure participant is ready
          setTimeout(() => {
            sendSyncEvent({
              videoId: videoSync.videoId,
              time: currentTime,
              isPlaying: isPlaying,
              isForceSync: false, // This is sync data sharing, not force sync
            });
          }, 1000);
        }
      }
    };

    meeting.participants.joined.on("participantJoined", onParticipantJoined);

    // Note: Cloudflare Realtime Kit handles participant events differently
    // We'll rely on the whisper system for participant communication
    // No need to manually listen for participant events here
  }, [isStudent, meeting, videoSync, isPlayerReady, sendSyncEvent]);

  // Player event handlers
  const onPlayerReady = (event: any) => {
    console.log("YouTube player ready");
    playerRef.current = event.target;
    setIsPlayerReady(true);
    setIsVideoError(false);

    // Set initial volume
    event.target.setVolume(100);

    // Sync immediately with current state if available
    if (videoSync) {
      event.target.seekTo(videoSync.time, true);
      if (videoSync.isPlaying) {
        event.target.playVideo();
      }
    }
  };

  const onPlayerStateChange = (event: any) => {
    // Don't send events if we're processing any received event or force sync
    if (isProcessingEventRef.current || isProcessingForceSyncRef.current || !videoSync) {
      console.log("Player state change blocked - processing event or force sync");

      return;
    }

    const state = event.data;
    const currentTime = playerRef.current?.getCurrentTime() || 0;

    // State: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
    let isPlaying = false;
    let shouldSendEvent = false;

    if (state === 1) {
      // Playing
      isPlaying = true;
      shouldSendEvent = true;
    } else if (state === 2 || state === 0) {
      // Paused or ended
      isPlaying = false;
      shouldSendEvent = true;
    }

    if (shouldSendEvent) {
      // Update local store
      setVideoSync({
        videoId: videoSync.videoId,
        time: currentTime,
        isPlaying: isPlaying,
        lastUpdate: Date.now(),
      });

      // Determine if this is a manual action or response to received sync
      const hasBlockedIds = blockedIdsRef.current.length > 0;

      console.log(
        `Player state change: ${isPlaying ? "playing" : "paused"}, hasBlockedIds: ${hasBlockedIds}`,
      );

      // Send to other participants
      sendSyncEvent({
        videoId: videoSync.videoId,
        time: currentTime,
        isPlaying: isPlaying,
        isForceSync: !hasBlockedIds, // Force sync if manual, regular sync if responding to received event
      });

      // Clear blocked IDs after sending (this was triggered by a received sync event)
      if (hasBlockedIds) {
        console.log("Clearing blocked IDs after responding to received sync");
        blockedIdsRef.current = [];
      }
    }
  };

  const onPlayerError = (event: any) => {
    console.error("YouTube player error:", event.data);
    setIsVideoError(true);
  };

  // Extract video ID from URL
  const getVideoId = (url: string) => {
    const match = url.match(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    );

    return match ? match[1] : null;
  };

  // Handle video URL submission (tutors only)
  const handleVideoUrlSubmit = (url: string) => {
    if (!channel() || !isTutor) return;

    const videoId = getVideoId(url);

    if (!videoId) return;

    const newVideoSync = {
      videoId,
      time: 0,
      isPlaying: false,
      lastUpdate: Date.now(),
    };

    setVideoSync(newVideoSync);

    // Send video set whisper to all participants
    channel().whisper("videoSet", {
      videoId,
      time: 0,
      isPlaying: false,
      senderId: userId,
      blockedIds: [],
    });
  };

  // Handle remove video (tutors only)
  const handleRemoveVideo = () => {
    if (!channel() || !isTutor) return;

    // Clear video sync locally
    setVideoSync(null);

    // Switch tutor back to call view immediately
    const { setView } = useClassroomStore.getState();

    setView("call");

    // Send video removed whisper to all participants
    channel().whisper("videoRemoved", {});
  };

  // Set up video sync whisper listeners
  useEffect(() => {
    if (!channel() || !lessonId || !userId) return;

    // Listen for video sync updates
    channel().listenForWhisper("videoSync", handleReceivedSyncData);

    // Listen for force sync events (students only)
    if (!isTutor) {
      channel().listenForWhisper("forceSync", handleForceSyncEvent);
    }

    // Listen for sync data requests (tutors only)
    if (isTutor) {
      channel().listenForWhisper("requestData", handleSyncDataRequest);
    }

    // Listen for video set events
    channel().listenForWhisper("videoSet", (data: any) => {
      console.log("Received video set whisper:", data);

      setVideoSync({
        videoId: data.videoId,
        time: data.time || 0,
        isPlaying: data.isPlaying || false,
        lastUpdate: Date.now(),
      });

      // Students should automatically switch to video view
      if (!isTutor) {
        const { setView } = useClassroomStore.getState();

        setView("video");
      }
    });

    // Video removed events are handled in PipModeProvider to ensure all users switch back to call view

    return () => {
      channel().stopListeningForWhisper("videoSync");
      channel().stopListeningForWhisper("forceSync");
      channel().stopListeningForWhisper("requestData");
      channel().stopListeningForWhisper("videoSet");
    };
  }, [channel, lessonId, userId, isTutor]);

  // Cleanup intervals and timeouts
  useEffect(() => {
    return () => {
      if (autoSyncIntervalRef.current) {
        clearInterval(autoSyncIntervalRef.current);
      }
      if (syncButtonTimeoutRef.current) {
        clearTimeout(syncButtonTimeoutRef.current);
      }
      if (eventDebounceTimeoutRef.current) {
        clearTimeout(eventDebounceTimeoutRef.current);
      }
    };
  }, []);

  // Reset sync state when video changes
  useEffect(() => {
    setShowSyncButton(false);
    blockedIdsRef.current = []; // Reset blocked IDs
    isProcessingEventRef.current = false; // Reset processing flags
    isProcessingForceSyncRef.current = false;
    lastEventTimeRef.current = 0; // Reset event timing

    // Clear any pending debounced events
    if (eventDebounceTimeoutRef.current) {
      clearTimeout(eventDebounceTimeoutRef.current);
      eventDebounceTimeoutRef.current = null;
    }
  }, [videoSync?.videoId]);

  // Render empty state for tutors
  if (!videoSync && isTutor) {
    return (
      <>
        <div className="flex h-full w-full items-center justify-center">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="bg-muted/20 flex h-16 w-16 items-center justify-center rounded-full">
                <Icon
                  className="text-muted-foreground"
                  height="32"
                  icon="lucide:play-circle"
                  width="32"
                />
              </div>
            </div>
            <h3 className="text-accent-foreground mb-2 text-lg font-semibold">
              {t("classroom:videoRoom")}
            </h3>
            <p className="text-muted-foreground mb-4 text-sm">{t("classroom:videoRoomEmpty")}</p>
            <Button onClick={() => setShowVideoDialog(true)}>
              <Icon className="mr-2" height="16" icon="lucide:plus" width="16" />
              {t("classroom:addVideo")}
            </Button>
          </div>
        </div>

        <VideoLinkDialog
          isOpen={showVideoDialog}
          onClose={() => setShowVideoDialog(false)}
          onSuccess={handleVideoUrlSubmit}
        />
      </>
    );
  }

  // Render empty state for students
  if (!videoSync && !isTutor) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="bg-muted/20 flex h-16 w-16 items-center justify-center rounded-full">
              <Icon
                className="text-muted-foreground"
                height="32"
                icon="lucide:play-circle"
                width="32"
              />
            </div>
          </div>
          <h3 className="text-accent-foreground mb-2 text-lg font-semibold">
            {t("classroom:videoRoom")}
          </h3>
          <p className="text-muted-foreground text-sm">{t("classroom:waitingForVideo")}</p>
        </div>
      </div>
    );
  }

  if (!videoSync) return null;

  return (
    <>
      <motion.div
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          "relative h-full w-full overflow-hidden rounded-lg",
          compact ? "bg-side-accent" : "bg-black",
        )}
        initial={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.4, type: "spring", stiffness: 200, damping: 20 }}
      >
        {videoSync.videoId && !isVideoError ? (
          <YouTube
            className="h-full w-full" // Removed pointer-events-none for students
            iframeClassName="w-full h-full" // Both students and tutors can interact
            opts={opts}
            videoId={videoSync.videoId}
            onEnd={onPlayerStateChange}
            onError={onPlayerError}
            onPause={onPlayerStateChange}
            onPlay={onPlayerStateChange}
            onReady={onPlayerReady}
            onStateChange={onPlayerStateChange}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <div className="text-center">
              <Icon
                className="text-destructive mb-4"
                height="48"
                icon="lucide:alert-triangle"
                width="48"
              />
              <p className="text-accent-foreground">{t("classroom:videoLoadError")}</p>
            </div>
          </div>
        )}

        {/* Tutor controls only */}
        {isTutor && (
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              className="h-8 w-8 border-white/20 bg-black/60 p-0 text-white backdrop-blur-sm hover:bg-black/70"
              size="icon"
              title={t("classroom:changeVideo")}
              variant="secondary"
              onClick={() => setShowVideoDialog(true)}
            >
              <Icon height="14" icon="lucide:edit" width="14" />
            </Button>
            <Button
              className="h-8 w-8 border-white/20 bg-red-600/80 p-0 text-white backdrop-blur-sm hover:bg-red-600/90"
              size="icon"
              title={t("classroom:removeVideo")}
              variant="destructive"
              onClick={handleRemoveVideo}
            >
              <Icon height="14" icon="lucide:trash" width="14" />
            </Button>
          </div>
        )}

        {/* Sync Button for Students */}
        {!isTutor && (
          <AnimatePresence>
            {showSyncButton && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="absolute bottom-4 left-1/2 -translate-x-1/2"
                exit={{ opacity: 0, y: 10 }}
                initial={{ opacity: 0, y: 10 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                <Button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 rounded-full px-4 py-2 shadow-lg backdrop-blur-sm"
                  onClick={requestSyncFromTutor}
                >
                  <Icon height="16" icon="lucide:refresh-cw" width="16" />
                  {t("classroom:clickToSyncWithTutor")}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </motion.div>

      {/* Video link dialog for tutor */}
      {isTutor && (
        <VideoLinkDialog
          initialUrl={videoSync ? `https://www.youtube.com/watch?v=${videoSync.videoId}` : null}
          isOpen={showVideoDialog}
          onClose={() => setShowVideoDialog(false)}
          onSuccess={handleVideoUrlSubmit}
        />
      )}
    </>
  );
}
